import { IconButton, InputAdornment, TextField } from "@mui/material"
import { Close } from "@mui/icons-material"
import SearchIcon from "@mui/icons-material/Search"
import { AnimatePresence, motion } from "framer-motion"
import React, { ChangeEvent, forwardRef, memo, useState } from "react"
import styled from "styled-components"
import { useClickOutside } from "../../../hooks/useClickOutside"
import { theme } from "../../../theme"
import { useDebounce } from "../../../hooks/useDebounce"
import { ButtonText } from "../button"

const MINIMAL_SEARCH_LENGTH = 3

export type PageSearchProps = {
  value: string
  onChange: (newValue: string) => void
  onClear: () => void
  onClearSideEffects?: () => void
  label?: string
  placeholder?: string
  onSubmit: () => void
  dynamicWidth?: boolean
  className?: string
  isFocused?: boolean
  isExpanded?: boolean
  alwaysShowClearButton?: boolean
  onFocus?: (value: boolean) => void
  skipRefs?: React.RefObject<HTMLElement>[]
  customSearchBarWidth?: string
  dataCy?: string
  iconWhenShrinked?: boolean
}

export const PageSearch = memo(
  forwardRef<HTMLInputElement, PageSearchProps>((props, ref) => {
    const {
      value = "",
      onChange,
      onClear,
      onClearSideEffects,
      label,
      placeholder,
      onSubmit,
      dynamicWidth = false,
      className,
      isExpanded = false,
      alwaysShowClearButton = false,
      skipRefs,
      customSearchBarWidth,
      dataCy,
      iconWhenShrinked,
    } = props

    const [submitable, setSubmitable] = useState(false)
    const [isFocused, setIsFocused] = useState(props.isFocused)

    const onFocus = (isFocusNeeded: boolean) => {
      props.onFocus && props.onFocus(isFocusNeeded)
      setIsFocused(isFocusNeeded)
    }

    const handleSubmit = useDebounce(() => {
      if (submitable) {
        onSubmit()
      }
    }, 500)

    const refClickOutside = useClickOutside(() => {
      if (value === "" && isFocused) {
        onFocus(false)
      }
    }, skipRefs)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.length >= MINIMAL_SEARCH_LENGTH || e.target.value.length === 0) {
        setSubmitable(true)
        handleSubmit()
      } else {
        setSubmitable(false)
      }

      onChange(e.target.value)
    }

    const handleFocus = () => onFocus(true)

    const handleClear = () => {
      onClear()
      onFocus && onFocus(false)
      onClearSideEffects && onClearSideEffects()
    }

    const isFullWidth = dynamicWidth && isExpanded
    const showClearButton = !!value || isExpanded || alwaysShowClearButton
    const searchBarWidth = customSearchBarWidth ?? "320px"
    const hideLabel = !isExpanded && !!value

    return (
      <Root className={className} $isFullWidth={isFullWidth}>
        {iconWhenShrinked && !isFocused ? (
          <ButtonText
            icon={<SearchIcon />}
            onClick={() => {
              handleFocus()
            }}
          />
        ) : (
          <CustomTextField
            autoFocus={isFocused}
            ref={refClickOutside}
            inputRef={ref}
            variant="outlined"
            label={hideLabel ? "" : label}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            initial={false}
            animate={isFullWidth ? "fullWidth" : "normal"}
            variants={{ normal: { width: searchBarWidth }, fullWidth: { width: "100%" } }}
            transition={{ type: "linear", duration: 0.3 }}
            InputLabelProps={{
              shrink: isExpanded,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CustomSearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <AnimatePresence>
                    {showClearButton && (
                      <ClearButton
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "linear", duration: 0.3 }}
                        onClick={handleClear}
                      >
                        <Close />
                      </ClearButton>
                    )}
                  </AnimatePresence>
                </InputAdornment>
              ),
            }}
            data-cy={dataCy}
          />
        )}
      </Root>
    )
  })
)

const Root = styled.div<{ $isFullWidth: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex: ${(props) => (props.$isFullWidth ? "1 1 100%" : "none")};
  flex-direction: column;
`

const CustomTextField = styled(motion(TextField))`
  input {
    height: ${theme.height.m};
    padding: 16px 0;
  }
  background-color: ${theme.color.white};

  .MuiInputLabel-root[data-shrink="false"] {
    margin-left: ${theme.spacing.xl};
  }
`

const CustomSearchIcon = styled(SearchIcon)`
  color: ${theme.color.grey2};
`

const ClearButton = styled(motion(IconButton))`
  color: ${theme.color.grey2};
  font-size: 10px;
  height: ${theme.iconSize.l};
  width: ${theme.iconSize.l};

  svg {
    height: ${theme.iconSize.l};
  }
`
